import { useEffect, useState } from 'react';
import {
    Alert, Autocomplete, Box, FormControl, FormHelperText, IconButton,
    Input, InputLabel, Stack, TextField, Tooltip, Typography
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

const ShipOrder = ({ selectedOrder, partnerOptions = [], onTransition }) => {
    const { id, shipping = {} } = selectedOrder;
    const [values, setValues] = useState({ partner: '', tracking: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => setValues({
        partner: shipping.partner || '', tracking: shipping.tracking_guide || ''
    }), [id, shipping.partner, shipping.tracking_guide]);

    const pasteFromClipboard = async () => {
        if (!navigator.clipboard?.readText) return;
        try {
            const tracking = (await navigator.clipboard.readText()).trim().slice(0, 64);
            setValues(current => ({ ...current, tracking }));
        } catch (clipboardError) {
            setError('No fue posible leer el portapapeles.');
        }
    };

    const submit = async () => {
        const partner = values.partner.trim().toUpperCase();
        const tracking = values.tracking.trim();
        if (partner.length < 3 || tracking.length < 3) {
            setError('Captura una paquetería y una guía válidas.');
            return;
        }
        setError('');
        setLoading(true);
        try {
            await onTransition(id, { action: 'ship', partner, tracking });
        } catch (requestError) {
            setError(requestError.message === 'shipping_window_expired'
                ? 'El periodo de 30 días para marcar el pedido como enviado ya venció.'
                : requestError.message === 'invalid_state'
                    ? 'El pedido cambió de estado. Actualiza la información.'
                    : 'No fue posible actualizar el pedido.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Stack spacing={2} alignItems="flex-start">
            {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}
            <Autocomplete freeSolo options={partnerOptions} value={values.partner}
                onInputChange={(_event, value) => setValues(current => ({
                    ...current, partner: value.toUpperCase().slice(0, 64)
                }))}
                renderInput={params => <TextField {...params} label="Paquetería" variant="standard" />}
                sx={{ minWidth: 240 }} />
            <Box>
                <FormControl variant="standard">
                    <InputLabel htmlFor={`tracking-${id}`}>Guía de seguimiento</InputLabel>
                    <Input id={`tracking-${id}`} value={values.tracking}
                        inputProps={{ maxLength: 64 }}
                        onChange={event => setValues(current => ({
                            ...current, tracking: event.target.value
                        }))}
                        onKeyDown={event => { if (event.key === 'Enter') submit(); }} />
                    <FormHelperText>Tracking</FormHelperText>
                </FormControl>
                <IconButton onClick={pasteFromClipboard} aria-label="Pegar guía"><ContentPasteIcon /></IconButton>
            </Box>
            <Stack direction="row" alignItems="center">
                <LoadingButton endIcon={<LocalShippingIcon />} loading={loading}
                    loadingPosition="end" variant="contained" onClick={submit}>
                    Marcar enviado
                </LoadingButton>
                <Tooltip title={<Typography variant="caption">Actualiza el pedido y envía el correo sólo una vez.</Typography>}>
                    <IconButton><HelpOutlineIcon /></IconButton>
                </Tooltip>
            </Stack>
        </Stack>
    );
};

export default ShipOrder;
